import type { IUserPermissions } from 'src/models/permission/permission';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { PermissionLevel } from 'src/models/permission/permission';

interface PermissionState {
  permissions: IUserPermissions | null;
  isLoading: boolean;

  // Actions
  setPermissions: (permissions: IUserPermissions) => void;
  clearPermissions: () => void;
  setLoading: (loading: boolean) => void;

  // 메인 메뉴 권한 체크
  getMenuPermissionLevel: (menuName: string) => PermissionLevel;
  canViewMenu: (menuName: string) => boolean;
  canEditMenu: (menuName: string) => boolean;

  // 서브 메뉴 권한 체크
  getSubMenuPermissionLevel: (menuName: string, submenuName: string) => PermissionLevel;
  canViewSubMenu: (menuName: string, submenuName: string) => boolean;
  canEditSubMenu: (menuName: string, submenuName: string) => boolean;

  // NavList용 checkPermissions (메뉴/서브메뉴 통합)
  // allowedRoles 형식: "메뉴명" 또는 "메뉴명/서브메뉴명"
  // 반환값: true = 숨김, false = 표시
  checkPermissions: (allowedRoles?: string | string[]) => boolean;
}

export const usePermissionStore = create<PermissionState>()(
  persist(
    (set, get) => ({
      permissions: null,
      isLoading: true,

      setPermissions: (permissions) => set({ permissions, isLoading: false }),
      clearPermissions: () => set({ permissions: null, isLoading: false }),
      setLoading: (loading) => set({ isLoading: loading }),

      // 메인 메뉴 권한 레벨 조회
      getMenuPermissionLevel: (menuName) => {
        const { permissions } = get();
        const perm = permissions?.menu_permissions.find((p) => p.menu_name === menuName);
        return perm?.permission_level ?? PermissionLevel.HIDDEN;
      },

      // 메인 메뉴 조회 가능 여부 (level >= 1)
      canViewMenu: (menuName) =>
        get().getMenuPermissionLevel(menuName) >= PermissionLevel.READ_ONLY,

      // 메인 메뉴 편집 가능 여부 (level >= 2)
      canEditMenu: (menuName) => get().getMenuPermissionLevel(menuName) >= PermissionLevel.WRITE,

      // 서브 메뉴 권한 레벨 조회
      getSubMenuPermissionLevel: (menuName, submenuName) => {
        const { permissions } = get();
        const perm = permissions?.submenu_permissions.find(
          (p) => p.menu_name === menuName && p.submenu_name === submenuName
        );
        return perm?.permission_level ?? PermissionLevel.HIDDEN;
      },

      // 서브 메뉴 조회 가능 여부 (level >= 1)
      canViewSubMenu: (menuName, submenuName) =>
        get().getSubMenuPermissionLevel(menuName, submenuName) >= PermissionLevel.READ_ONLY,

      // 서브 메뉴 편집 가능 여부 (level >= 2)
      canEditSubMenu: (menuName, submenuName) =>
        get().getSubMenuPermissionLevel(menuName, submenuName) >= PermissionLevel.WRITE,

      // NavList용 권한 체크
      // allowedRoles가 "메뉴명" 형식이면 메인 메뉴 권한 체크
      // allowedRoles가 "메뉴명/서브메뉴명" 형식이면 서브 메뉴 권한 체크
      checkPermissions: (allowedRoles) => {
        // allowedRoles가 없으면 모두 표시 (숨기지 않음)
        if (!allowedRoles) return false;

        const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

        // 모든 역할에 대해 권한이 없으면 숨김 (true 반환)
        return roles.every((role) => {
          if (role.includes('/')) {
            const [menuName, submenuName] = role.split('/');
            return !get().canViewSubMenu(menuName, submenuName);
          }
          return !get().canViewMenu(role);
        });
      },
    }),
    {
      name: 'permission-storage',
      partialize: (state) => ({
        permissions: state.permissions,
      }),
    }
  )
);
